import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";

// `Notifications.DateCreated` comes back as Bahrain local time (UTC+3, no DST)
// with NO timezone suffix — e.g. "2026-07-23T09:15:22.4130000". Handing that
// string to `new Date()` makes the browser read it as the *viewer's* local
// time, which silently shifts every timestamp for anyone outside Bahrain.
//
// So the offset is applied explicitly here. Bahrain has never observed DST, so
// a fixed +3 is exact rather than an approximation.
const BAHRAIN_UTC_OFFSET_HOURS = 3;

// Also tolerates the sub-second precision SQL emits (7 digits, where JS takes
// 3), and a trailing Z or explicit offset in case the API ever starts sending
// one — if it does, that value is authoritative and no offset is applied.
const TIMESTAMP_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d+))?$/;

export const parseBahrainTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;

  const raw = String(value).trim();

  // Already carries a zone — let the engine handle it.
  if (/(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)) {
    const zoned = new Date(raw);
    return Number.isNaN(zoned.getTime()) ? null : zoned;
  }

  const match = TIMESTAMP_PATTERN.exec(raw);
  if (!match) {
    const fallback = new Date(raw);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  const [, year, month, day, hours, minutes, seconds, fraction] = match;
  const milliseconds = fraction ? Number(fraction.slice(0, 3).padEnd(3, "0")) : 0;

  const utc = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
    Number(seconds || 0),
    milliseconds
  );

  const parsed = new Date(utc - BAHRAIN_UTC_OFFSET_HOURS * 60 * 60 * 1000);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Arabic noun agreement after a number, which has four cases rather than the
 * singular/plural pair English gets away with:
 *
 *   1      singular, numeral dropped   قبل دقيقة      (not "قبل 1 دقيقة")
 *   2      dual, numeral dropped       قبل دقيقتين    (not "قبل 2 دقائق")
 *   3–10   plural of paucity           قبل 5 دقائق
 *   11+    singular accusative         قبل 15 دقيقة
 *
 * Getting this wrong is the kind of thing that reads as machine-translated to a
 * native speaker even when every word is correct.
 */
const arabicAgo = (count, { one, two, few, many }) => {
  if (count === 1) return `قبل ${one}`;
  if (count === 2) return `قبل ${two}`;
  if (count <= 10) return `قبل ${count} ${few}`;
  return `قبل ${count} ${many}`;
};

const AR_MINUTE = {
  one: "دقيقة",
  two: "دقيقتين",
  few: "دقائق",
  many: "دقيقة",
};
const AR_HOUR = { one: "ساعة", two: "ساعتين", few: "ساعات", many: "ساعة" };
const AR_DAY = { one: "يوم", two: "يومين", few: "أيام", many: "يومًا" };

/**
 * Renders a notification timestamp the way an inbox should: relative while it
 * is still recent, absolute once "5 days ago" stops being more useful than the
 * date itself.
 *
 * @param {string} value  raw DateCreated from the API
 * @param {(en: string, ar: string) => string} translate  useLanguageTranslate
 * @param {string} language  i18n.resolvedLanguage, for the absolute format
 */
export const formatNotificationTimestamp = (value, translate, language) => {
  const date = parseBahrainTimestamp(value);
  if (!date) return "";

  const elapsed = Date.now() - date.getTime();

  // A clock skew between device and server can make a fresh notification look
  // like it arrived in the future; show it as "now" rather than "in 2 minutes".
  if (elapsed < MINUTE) return translate("Just now", "الآن");

  if (elapsed < HOUR) {
    const minutes = Math.floor(elapsed / MINUTE);
    return translate(`${minutes}m ago`, arabicAgo(minutes, AR_MINUTE));
  }

  if (elapsed < DAY) {
    const hours = Math.floor(elapsed / HOUR);
    return translate(`${hours}h ago`, arabicAgo(hours, AR_HOUR));
  }

  if (elapsed < 7 * DAY) {
    const days = Math.floor(elapsed / DAY);
    return translate(`${days}d ago`, arabicAgo(days, AR_DAY));
  }

  return format(date, "d MMM yyyy", {
    locale: language === "ar" ? ar : enUS,
  });
};

export default formatNotificationTimestamp;
